import { memo, PropsWithChildren, Suspense, useEffect, useMemo } from 'react'
import { GraphQLProvider } from '../api/v1/client/graphql'
import { hostCallbacks, setPlatformGlobals } from '../globals'
import { useApiClientInit, useHorizonContext, useHostContext } from '../hooks'
import { ApiClientContextProvider } from './api-client-context'
import { EventBusContextProvider } from './event-bus-context'
import { HostContextProvider } from './host-context'
import { PageStateProvider } from './page-state-provider'
import { RegistryContextProvider } from './registry-context'
import { HorizonConfig, HorizonContext, HorizonProviderProps } from './types'

const HorizonInit = ({
  onInit,
}: {
  onInit?: (context: HorizonContext) => void
}) => {
  const context = useHorizonContext()

  useEffect(() => {
    onInit?.(context)
  }, [])

  return null
}

/**
 * Provides all Horizon Contexts to its descendants.
 *
 * Individual context values can be overridden by passing a new value prop. All
 * contexts are re-provided anyway, so if there is no new value it will read the
 * current value and pass it down.
 *
 * This Provider can be used to reinitialize an entire React tree from a root,
 * and is not recommended or expected to be used in the middle of a tree.
 *
 * @internal Not intended for direct use outside of Horizon Core.
 */
export const HorizonProvider = ({
  children,
  onInit,
  ...newContext
}: PropsWithChildren<HorizonProviderProps>) => {
  const { validateAndMerge } = useHorizonContext()

  const { host, eventBus, apiClient, registry } = validateAndMerge(newContext)

  return (
    <HostContextProvider value={host}>
      <EventBusContextProvider value={eventBus}>
        <ApiClientContextProvider value={apiClient}>
          <GraphQLProvider>
            <RegistryContextProvider value={registry}>
              <PageStateProvider>
                <HorizonInit onInit={onInit} />
                {/* PlatformComponent uses Suspense. We should probably set a fallback. */}
                <Suspense fallback={null}>{children}</Suspense>
              </PageStateProvider>
            </RegistryContextProvider>
          </GraphQLProvider>
        </ApiClientContextProvider>
      </EventBusContextProvider>
    </HostContextProvider>
  )
}

/**
 * Initializes HorizonContext with root-level instances.
 *
 * Uses the supplied configuration object to construct environment-specific
 * variants of some services (e.g. ApiClient).
 */
export const HorizonRootProvider = memo(
  ({
    children,
    auth,
    platformApiDomain,
    service,
    eventBus,
    callbacks,
    onInit,
    ...config
  }: PropsWithChildren<HorizonConfig>) => {
    const host = useHostContext()
    const apiClient = useApiClientInit({ auth, platformApiDomain, service })

    const globalEventBus = useMemo(() => {
      if (config.globalEventBus) {
        setPlatformGlobals({ globalEventBus: config.globalEventBus })

        return config.globalEventBus
      }

      return host.globalEventBus
    }, [config.globalEventBus])

    /**
     * There should only ever be one Callbacks instance, and a root provider is
     * the most appropriate place to initialize it. We make it reactive to
     * config changes so the host can potentially avoid a full reload.
     */
    useEffect(() => {
      if (!callbacks) return
      hostCallbacks.init(callbacks)
    }, [callbacks])

    return (
      <HorizonProvider
        apiClient={apiClient}
        eventBus={eventBus}
        host={{ ...host, globalEventBus }}
        onInit={onInit}
      >
        {children}
      </HorizonProvider>
    )
  },
)

HorizonRootProvider.displayName = 'HorizonRootProvider'
